import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { useAppStore } from "@/hooks/useAppStore";
import { useTranslation } from "@/lib/translate";

interface JoinRoomDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  isAdmin?: boolean;
}

export const JoinRoomDialog: React.FC<JoinRoomDialogProps> = ({
  open,
  onOpenChange,
  isAdmin
}) => {
  const [roomCode, setRoomCode] = useState("");
  const [error, setError] = useState("");
  const currentRoomId = useAppStore((state) => state.roomId);
  const redirectToRoom = useAppStore((state) => state.redirectToRoom);
  const preferredLanguage = useAppStore((state) => state.preferredLanguage);
  const t = useTranslation(preferredLanguage);

  useEffect(() => {
    // Clear state when dialog opens or closes
    if (!open) {
      setRoomCode("");
      setError("");
    }
  }, [open]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");
    const code = roomCode.trim().toUpperCase();

    if (!code || code.length !== 7) {
      // Example validation
      setError(t("Invalid Room Code (must be 7 characters)."));
      return;
    }

    if (code === currentRoomId) {
      setError(t("You are already in this room."));
      return;
    }

    try {
      isAdmin ? redirectToRoom(code) : redirectToRoom(code);
      onOpenChange(false); // Close dialog on successful join attempt
    } catch (err: any) {
      // Error handling might be done within joinRoom action,
      // but we can also catch potential issues here.
      setError(err.message || t("Failed to join room."));
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[425px] bg-neutral-800 border-neutral-700 text-white">
        <DialogHeader>
          <DialogTitle>{t("Join Room")}</DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit}>
          <div className="grid gap-4 py-4">
            <div className="grid grid-cols-4 items-center gap-4">
              <Label htmlFor="room-code" className="text-right">
                {t("Room Code")}
              </Label>
              <Input
                id="room-code"
                value={roomCode}
                onChange={(e) => setRoomCode(e.target.value)}
                className="col-span-3 bg-neutral-700 border-neutral-600 placeholder:text-neutral-400 focus:ring-chotto-gold"
                placeholder={t("Enter 7-character code")}
                maxLength={7} // Enforce length
                autoCapitalize="characters"
                autoFocus
              />
            </div>
            {error && (
              <p className="text-red-400 text-sm text-center col-span-4">
                {error}
              </p>
            )}
          </div>
          <DialogFooter>
            <Button
              type="button"
              variant="secondary"
              onClick={() => onOpenChange(false)}
            >
              {t("Cancel")}
            </Button>
            <Button
              type="submit"
              className="bg-chotto-share-blue hover:bg-chotto-share-blue/90"
            >
              {t("Join Room")}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};
